import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useState, useCallback, useMemo, useRef } from 'react';
import {
  Radar,
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
  LegendPayload,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { useTranslation } from '@/hooks/useTranslation';
import type { ClassificationOpportunityAndRisk } from '@/types/classification';
import { legend } from '@/utils/functions/legends';
import { numberFormat } from '@/utils/functions/numberFormat';
import { useLanguage } from '@/hooks/useLanguage';
import { getDistinctVibrantColors } from '@/utils/functions/colors';
import { StyledTooltip, StyledTooltipLabel } from './styles';

interface DataItem {
  subject: string;
  [key: string]: any;
}

interface DataSeries {
  dataKey: string;
  name: string;
  fillColor: string;
  strokeColor: string;
}

interface RadarChartProps {
  municipalityName?: string;
  data: DataItem[];
  series: DataSeries[];
  maxValue?: number;
}

export const RadarChart = ({ data, series, municipalityName }: RadarChartProps) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();

  const [hoveringDataKey, setHoveringDataKey] = useState<DataKey<any> | null | undefined>(null);

  const [disabledDataKeys, setDisabledDataKeys] = useState<Set<string>>(new Set());

  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Custom tooltip component to prevent flickering
  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }

      if (!active || !payload || !payload.length) {
        return null;
      }

      return (
        <StyledTooltip>
          <StyledTooltipLabel>{label}</StyledTooltipLabel>
          {payload.map((data: any, index: number) => {
            const classificationKey = `${data.dataKey}_classification`;
            const classification = data.payload[
              classificationKey
            ] as ClassificationOpportunityAndRisk;

            let formattedValue;
            if (classification) {
              formattedValue = legend.legendByClassification({
                classification,
                t,
              });
            } else {
              formattedValue = numberFormat.format(data.value as number, currentLanguage);
            }

            return (
              <div key={index} style={{ color: data.color }}>
                {data.name}: {formattedValue}
              </div>
            );
          })}
        </StyledTooltip>
      );
    },
    [theme, t, currentLanguage]
  );

  const radiusLabels = [
    { value: 0, label: '' },
    { value: 1, label: t('label.label.low') },
    { value: 2, label: t('label.label.medium_low') },
    { value: 3, label: t('label.label.medium_high') },
    { value: 4, label: t('label.label.high') },
  ];

  const getOpacity = useMemo(
    () => (dataKey: string) => {
      const isDisabled = disabledDataKeys.has(dataKey);

      if (isDisabled) {
        return {
          strokeOpacity: 0,
          fillOpacity: 0,
        };
      }

      return {
        strokeOpacity: hoveringDataKey === dataKey ? 0.5 : 1,
        fillOpacity: hoveringDataKey === dataKey ? 0.75 : 0.5,
      };
    },
    [disabledDataKeys, hoveringDataKey]
  );

  const handleMouseEnter = useCallback((payload: LegendPayload) => {
    setHoveringDataKey(payload.dataKey);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveringDataKey(null);
  }, []);

  const handleRadarMouseEnter = useCallback((dataKey: string) => {
    setHoveringDataKey(dataKey);
  }, []);

  const handleRadarMouseLeave = useCallback(() => {
    setHoveringDataKey(null);
  }, []);

  const handleLegendClick = useCallback((payload: LegendPayload) => {
    const dataKey = String(payload.dataKey);
    setDisabledDataKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dataKey)) {
        newSet.delete(dataKey);
      } else {
        newSet.add(dataKey);
      }
      return newSet;
    });
  }, []);

  // Ensure all series have distinct colors (stroke/fill). If duplicates or missing, assign from palette.
  const normalizedSeries = useMemo(() => {
    const palette = getDistinctVibrantColors(series.length);
    const usedStrokeColors = new Set<string>();
    let paletteIndex = 0;

    return series.map((s, idx) => {
      let stroke = s.strokeColor;
      let fill = s.fillColor;

      const needsColor = !stroke || usedStrokeColors.has(stroke);
      if (needsColor) {
        // pick next available palette color not yet used
        while (
          paletteIndex < palette.length &&
          usedStrokeColors.has(palette[paletteIndex].strokeColor)
        ) {
          paletteIndex += 1;
        }
        const candidate = palette[paletteIndex] || palette[Math.min(idx, palette.length - 1)];
        stroke = candidate.strokeColor;
        fill = candidate.fillColor;
        paletteIndex += 1;
      }

      usedStrokeColors.add(stroke);
      return { ...s, strokeColor: stroke, fillColor: fill } as DataSeries;
    });
  }, [series]);

  const renderLegend = useCallback(
    (props: any) => {
      const { payload } = props;

      const sortedPayload = useMemo(() => {
        if (!municipalityName) return payload;
        const cloned = [...payload];
        cloned.sort((a, b) => {
          const aMatch = String(a?.value) === municipalityName;
          const bMatch = String(b?.value) === municipalityName;
          if (aMatch === bMatch) return 0;
          return aMatch ? -1 : 1;
        });
        return cloned;
      }, [payload, municipalityName]);

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            padding: '8px 0 8px',
          }}
        >
          {sortedPayload.map((entry: any, index: number) => {
            const isDisabled = disabledDataKeys.has(String(entry.dataKey));

            return (
              <div
                key={`item-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  opacity: isDisabled ? 0.4 : 1,
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={() => handleMouseEnter(entry)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleLegendClick(entry)}
              >
                <svg width="14" height="14" style={{ display: 'block' }}>
                  <circle
                    cx="7"
                    cy="7"
                    r="6"
                    fill={entry.color}
                    style={{
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  />
                </svg>
                <span
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    textDecoration: isDisabled ? 'line-through' : 'none',
                  }}
                >
                  {entry.value}
                </span>
              </div>
            );
          })}
        </div>
      );
    },
    [disabledDataKeys, handleMouseEnter, handleMouseLeave, handleLegendClick, municipalityName]
  );

  return (
    <ResponsiveContainer width="100%" height="100%" minHeight="440px">
      <RechartsRadarChart
        cx="50%"
        cy={isMobile ? '30%' : '46%'}
        outerRadius={isTablet ? '60%' : '75%'}
        data={data}
      >
        <PolarGrid />

        <PolarAngleAxis
          dataKey="subject"
          tick={({ payload, x, y, textAnchor, cx, cy }) => {
            const words = String(payload.value).split(' ');
            const mid = Math.ceil(words.length / 2);

            const line1 = words.slice(0, mid).join(' ');
            const line2 = words.slice(mid).join(' ');

            // Calcula a direção do centro para o label
            const dx = Number(x) - Number(cx);
            const dy = Number(y) - Number(cy);
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Adiciona margem de 20px na direção do label
            const margin = isMobile ? 0 : 24;
            const offsetX = (dx / distance) * margin;
            const offsetY = (dy / distance) * margin;

            return (
              <text
                x={Number(x) + offsetX}
                y={Number(y) + offsetY}
                textAnchor={textAnchor}
                fontSize={12}
                fontWeight={700}
                fill="#666"
              >
                <tspan x={Number(x) + offsetX} dy="0">
                  {line1}
                </tspan>
                {line2 && (
                  <tspan x={Number(x) + offsetX} dy="1.2em">
                    {line2}
                  </tspan>
                )}
              </text>
            );
          }}
        />

        <PolarRadiusAxis
          domain={[0, 4]}
          angle={90}
          tickCount={5}
          tick={({ x, y, payload }) => {
            const labelData = radiusLabels.find((item) => item.value === payload.value);

            return (
              <text x={x} y={y} textAnchor="middle" fontSize={10} fill="#999">
                {labelData?.label || ''}
              </text>
            );
          }}
        />

        {normalizedSeries.map((serie) => {
          const { strokeOpacity, fillOpacity } = getOpacity(serie.dataKey);
          return (
            <Radar
              key={serie.dataKey}
              name={serie.name}
              dataKey={serie.dataKey}
              stroke={serie.strokeColor}
              strokeOpacity={strokeOpacity}
              fill={serie.fillColor}
              fillOpacity={fillOpacity}
              activeDot={{ r: 4, fill: serie.fillColor, stroke: 'none' }}
              dot={{ r: 2, fill: serie.fillColor }}
              onMouseEnter={() => handleRadarMouseEnter(serie.dataKey)}
              onMouseLeave={handleRadarMouseLeave}
            />
          );
        })}

        <Legend content={renderLegend} />

        <Tooltip
          content={CustomTooltip}
          allowEscapeViewBox={{ x: false, y: false }}
          animationDuration={0}
          isAnimationActive={false}
          offset={15}
          position={{ x: undefined, y: undefined }}
          wrapperStyle={{
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        />
      </RechartsRadarChart>
    </ResponsiveContainer>
  );
};
